import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Download, FileText, ExternalLink, ArrowRight } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PageLoader } from '../components/Spinner';
import { useDocumentTitle } from '../hooks/use-document-title';
import { API_URL, BACKEND_URL } from '../utils/api';
import { getErrorMessage } from '../utils/errors';

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-paper';
const eyebrow = 'text-xs sm:text-[13px] font-bold uppercase tracking-[0.16em] text-[#6E5B1C]';
const primaryPill = `inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-ink text-paper text-sm font-semibold hover:bg-ink/90 transition-colors ${focusRing}`;
const smallPill = `inline-flex items-center justify-center gap-1.5 h-9 min-w-[2.25rem] px-2.5 sm:px-4 rounded-full text-sm font-semibold transition-colors ${focusRing}`;
const primarySmall = `${smallPill} bg-ink text-paper hover:bg-ink/90`;
const secondarySmall = `${smallPill} gradient-border text-ink hover:text-[#6E5B1C]`;

const fileUrl = (url = '') => (url.startsWith('http') ? url : `${BACKEND_URL}${url}`);

/** Public page for a category's catalog — the link customers get by email/SMS. */
export default function CatalogPage() {
  const { categoryId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useDocumentTitle(data ? `${data.category.name} catalog` : 'Catalog');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_URL}/categories/${categoryId}/catalogs`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.status === 404
            ? 'This catalog is no longer available.'
            : getErrorMessage(err, 'We could not load this catalog.'));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [categoryId]);

  if (loading) return <PageLoader />;

  const catalogs = data?.catalogs || [];
  const images = catalogs.filter((c) => c.content_type?.startsWith('image/'));
  const pdfs = catalogs.filter((c) => c.content_type === 'application/pdf');

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <Navbar />
      <main id="main-content" className="flex-1 px-4 sm:px-8 pt-28 sm:pt-32 pb-20 sm:pb-28">
        <div className="max-w-5xl mx-auto">
          {error || !data ? (
            <div className="gradient-border gb-cream rounded-2xl text-center px-6 py-16 sm:py-20 max-w-2xl mx-auto">
              <p className={eyebrow}>Azamein catalog</p>
              <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-[-0.015em] text-ink mt-3 mb-3">
                Catalog <span className="italic font-medium">not found</span>
              </h1>
              <p className="text-[#4A4640] mb-8">{error || 'This catalog is no longer available.'}</p>
              <Link to="/templates" className={primaryPill}>
                Browse all designs <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          ) : (
            <>
              <header className="text-center mb-10 sm:mb-14 flex flex-col items-center gap-3">
                <p className={eyebrow}>Azamein catalog</p>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] font-semibold tracking-[-0.015em] text-ink break-words max-w-full">
                  {data.category.name}
                </h1>
                {data.category.description && (
                  <p className="text-base sm:text-[17px] text-[#4A4640] max-w-2xl">{data.category.description}</p>
                )}
              </header>

              {catalogs.length === 0 && (
                <div className="gradient-border gb-cream rounded-2xl text-center px-6 py-10 mb-10 text-[#4A4640]">
                  There are no catalog files here yet. You can still browse all our designs online.
                </div>
              )}

              {pdfs.length > 0 && (
                <section aria-label="Catalog documents" className="grid gap-4 sm:gap-5 sm:grid-cols-2 mb-10 sm:mb-14">
                  {pdfs.map((pdf) => (
                    <div
                      key={pdf.id}
                      className="min-w-0 flex items-center gap-3 sm:gap-4 rounded-2xl gradient-border gb-cream p-3 sm:p-5"
                    >
                      <div className="h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-xl bg-paper shadow-[0_10px_20px_-12px_rgba(26,26,26,0.45)] flex items-center justify-center">
                        <FileText className="h-6 w-6 sm:h-7 sm:w-7 text-ink" aria-hidden="true" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-ink truncate">{pdf.name}</p>
                        <p className="text-xs sm:text-[13px] text-[#6B665C]">PDF catalog</p>
                      </div>
                      <div className="flex gap-1.5 sm:gap-2 shrink-0">
                        <a
                          href={fileUrl(pdf.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${secondarySmall} gb-paper`}
                        >
                          <ExternalLink className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only sm:not-sr-only">View</span>
                        </a>
                        <a href={fileUrl(pdf.url)} download className={primarySmall}>
                          <Download className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only sm:not-sr-only">Download</span>
                        </a>
                      </div>
                    </div>
                  ))}
                </section>
              )}

              {images.length > 0 && (
                <section aria-label="Catalog pages" className="grid gap-6 sm:gap-8 sm:grid-cols-2 mb-10 sm:mb-14">
                  {images.map((img) => (
                    <figure key={img.id} className="min-w-0 flex flex-col gap-3">
                      <a
                        href={fileUrl(img.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`group block gradient-border gb-cream rounded-2xl p-3 sm:p-6 ${focusRing}`}
                      >
                        <img
                          src={fileUrl(img.url)}
                          alt={img.name}
                          loading="lazy"
                          className="w-full h-auto rounded-[4px] shadow-[0_18px_30px_-16px_rgba(26,26,26,0.45)] transition-transform duration-500 group-hover:-translate-y-1"
                        />
                      </a>
                      <figcaption className="px-1 text-sm font-semibold text-ink truncate">{img.name}</figcaption>
                    </figure>
                  ))}
                </section>
              )}

              <div className="relative overflow-hidden text-center rounded-[28px] bg-ink text-paper px-6 py-12 sm:py-16">
                <div className="glow-pink absolute -top-24 -left-24 h-72 w-72 pointer-events-none" aria-hidden="true" />
                <div className="glow-blue absolute -bottom-28 -right-20 h-80 w-80 pointer-events-none" aria-hidden="true" />
                <div className="relative">
                  <p className="text-xs sm:text-[13px] font-bold uppercase tracking-[0.16em] text-[#D9C27A] mb-3">
                    Ready when you are
                  </p>
                  <h2 className="font-serif text-3xl sm:text-5xl leading-[1.05] font-semibold tracking-[-0.015em] mb-3">
                    Found a design <span className="italic font-medium">you love?</span>
                  </h2>
                  <p className="text-paper/70 mb-8 max-w-md mx-auto">
                    Personalize it online in minutes, or call us to order by phone.
                  </p>
                  <Link
                    to="/templates"
                    className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full gradient-border-strong gb-ink text-paper text-[15px] font-bold hover:bg-white/5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper/80 focus-visible:ring-offset-2 focus-visible:ring-offset-ink"
                  >
                    Start your invitation <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
